import React, { useState, useEffect } from 'react';
import { Card, Button, LoadingSpinner, Alert } from './UIComponents';
import {
  listAzureDevOpsConfigs,
  createAzureDevOpsConfig,
  deleteAzureDevOpsConfig,
  testAzureDevOpsConnection,
  AzureDevOpsConfig
} from '../services/api';

interface AzureDevOpsConfigManagerProps {
  onConfigSelected: (configId: string) => void;
  selectedConfigId?: string;
}

export const AzureDevOpsConfigManager: React.FC<AzureDevOpsConfigManagerProps> = ({
  onConfigSelected,
  selectedConfigId
}) => {
  const [configs, setConfigs] = useState<AzureDevOpsConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);
  const [testingConfig, setTestingConfig] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    server_url: '',
    organization: '',
    project: '',
    token: '',
    area_path: '',
    iteration_path: ''
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const response = await listAzureDevOpsConfigs();
      setConfigs(response.configs);
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'Error al cargar configuraciones de Azure DevOps'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.server_url || !formData.organization || 
        !formData.project || !formData.token) {
      setAlert({
        type: 'warning',
        message: 'Por favor completa todos los campos obligatorios'
      });
      return;
    }

    setLoading(true);
    try {
      await createAzureDevOpsConfig(formData);
      setAlert({
        type: 'success',
        message: '✓ Configuración creada exitosamente'
      });
      setShowForm(false);
      setFormData({
        name: '',
        server_url: '',
        organization: '',
        project: '',
        token: '',
        area_path: '',
        iteration_path: ''
      });
      await loadConfigs();
    } catch (error) {
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Error al crear configuración'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (configId: string) => {
    if (!window.confirm('¿Estás seguro de eliminar esta configuración?')) {
      return;
    }

    setLoading(true);
    try {
      await deleteAzureDevOpsConfig(configId);
      setAlert({
        type: 'success',
        message: '✓ Configuración eliminada'
      });
      await loadConfigs();
      if (selectedConfigId === configId) {
        onConfigSelected('');
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'Error al eliminar configuración'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (configId: string) => {
    setTestingConfig(configId);
    try {
      const result = await testAzureDevOpsConnection(configId);
      if (result.success) {
        setAlert({
          type: 'success',
          message: `✓ ${result.message}`
        });
      } else {
        setAlert({
          type: 'error',
          message: `✗ ${result.message}`
        });
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'Error al probar conexión'
      });
    } finally {
      setTestingConfig(null);
    }
  };

  return (
    <Card className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gft-dark flex items-center">
          <span className="mr-3">⚙️</span>
          Configuración Azure DevOps
        </h3>
        <Button
          onClick={() => setShowForm(!showForm)}
          variant="outline"
          className="text-sm px-4 py-2"
        >
          {showForm ? '✕ Cancelar' : '+ Nueva Config'}
        </Button>
      </div>

      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Nombre de Configuración *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gft-purple focus:border-transparent"
              placeholder="Ej: Proyecto Bancolombia"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Server URL *
            </label>
            <input
              type="text"
              value={formData.server_url}
              onChange={(e) => setFormData({ ...formData, server_url: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gft-purple focus:border-transparent"
              placeholder="https://dev.azure.com o https://grupobancolombia.visualstudio.com"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Organización *
              </label>
              <input
                type="text"
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gft-purple focus:border-transparent"
                placeholder="mi-organizacion"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Proyecto *
              </label>
              <input
                type="text"
                value={formData.project}
                onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gft-purple focus:border-transparent"
                placeholder="Mi Proyecto"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Personal Access Token (PAT) *
            </label>
            <input
              type="password"
              value={formData.token}
              onChange={(e) => setFormData({ ...formData, token: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gft-purple focus:border-transparent font-mono text-sm"
              placeholder="Token de Azure DevOps"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Genera un PAT en Azure DevOps con permisos de Work Items (Read, Write, Manage)
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Area Path (Opcional)
              </label>
              <input
                type="text"
                value={formData.area_path}
                onChange={(e) => setFormData({ ...formData, area_path: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gft-purple focus:border-transparent"
                placeholder="Equipo/Subequipo"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Iteration Path (Opcional)
              </label>
              <input
                type="text"
                value={formData.iteration_path}
                onChange={(e) => setFormData({ ...formData, iteration_path: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gft-purple focus:border-transparent"
                placeholder="Sprint 1"
              />
            </div>
          </div>

          <Button
            type="submit"
            loading={loading}
            className="w-full"
            variant="primary"
          >
            💾 Guardar Configuración
          </Button>
        </form>
      )}

      {loading && !showForm ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="space-y-3">
          {configs.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No hay configuraciones guardadas</p>
              <p className="text-sm text-gray-400 mt-2">
                Crea una nueva configuración para publicar en Azure DevOps
              </p>
            </div>
          ) : (
            configs.map((config) => (
              <div
                key={config.id}
                className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  selectedConfigId === config.id
                    ? 'border-gft-purple bg-purple-50'
                    : 'border-gray-200 bg-white hover:border-gft-purple'
                }`}
                onClick={() => onConfigSelected(config.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 flex items-center">
                      {selectedConfigId === config.id && (
                        <span className="mr-2 text-gft-purple">✓</span>
                      )}
                      {config.name}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-semibold">Proyecto:</span> {config.project}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">Organización:</span> {config.organization}
                    </p>
                    <p className="text-sm text-gray-500 mt-1 font-mono text-xs">
                      {config.server_url}
                    </p>
                    {config.area_path && (
                      <p className="text-xs text-gray-500 mt-1">
                        <span className="font-semibold">Area:</span> {config.area_path}
                      </p>
                    )}
                    {config.iteration_path && (
                      <p className="text-xs text-gray-500">
                        <span className="font-semibold">Iteración:</span> {config.iteration_path}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col space-y-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTestConnection(config.id);
                      }}
                      loading={testingConfig === config.id}
                      variant="outline"
                      className="text-sm px-3 py-1"
                    >
                      {testingConfig === config.id ? 'Probando...' : '🔌 Probar'}
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(config.id);
                      }}
                      variant="outline"
                      className="text-sm px-3 py-1 text-red-600 hover:bg-red-50"
                    >
                      🗑️
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm">
        <p className="text-blue-800 font-semibold mb-1">💡 Tip:</p>
        <p className="text-blue-700">
          Selecciona una configuración para poder publicar el backlog generado en Azure DevOps Boards.
          La configuración incluye la URL del servidor, organización, proyecto y token de acceso.
        </p>
      </div>
    </Card>
  );
};
